/**
 * useTestingSession Hook
 *
 * Manages SSE connection to a testing session stream,
 * dispatching events to local state for the InteractiveTestingPanel.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import testingService from '../services/testing.service';
import {
  ChatMessage,
  TestingSessionPhase,
  TestingStepResult,
  TestingSSEEvent,
  TestingSessionStep,
  TestingEventCard,
  APIStepResult,
  APIDiscoveryResult,
  SiteTree,
  SiteTreeCoverage,
  CaptchaDetectedEvent,
  QualityAssessment,
  RetryDetail,
  FailureReviewData,
  ContextQuestions,
  StrategistInsight,
  AgentToolActivity,
  AgentInvestigationFindings,
  PageTestResult,
  TestCaseIdentified,
  SessionHistorySummary,
  TestingAction,
  ExistingCodeInfo,
} from '../types';

export interface MobileDeviceInfo {
  session_id: string;
  device_name?: string;
  platform?: string;
  app_package?: string;
  appium_url?: string;
}

export interface LoginFieldInfo {
  type: string;
  selector: string;
  label: string;
}

export type LoginStatus = 'none' | 'requested' | 'executing' | 'success' | 'failed';

export interface PriorResultsData {
  parent_session_id: string;
  total_steps: number;
  passed_count: number;
  failed_count: number;
  steps: Record<string, any>[];
}

export interface DiscoveredPage {
  node_id: string;
  url: string;
  title: string;
  depth: number;
  elements_count: number;
  forms_count: number;
  links_count: number;
  cached_priority?: number;
  cached_category?: string;
}

export interface UseTestingSessionReturn {
  phase: TestingSessionPhase;
  steps: TestingSessionStep[];
  stepResults: TestingStepResult[];
  liveStreamUrl: string | null;
  vncUrl: string | null;
  isApprovalPending: boolean;
  approvalMessage: string;
  domAnalysis: { elements_count: number; forms_count: number; links_count: number } | null;
  error: string | null;
  duration: number;
  idleRemaining: number | null;
  summary: {
    total_steps: number;
    passed: number;
    failed: number;
    video_url?: string;
    tms_entities?: any[];
  } | null;
  generatedCode: { files: any[]; summary: string; framework?: string; tms_test_case_id?: string } | null;
  apiStepResults: APIStepResult[];
  apiDiscovery: APIDiscoveryResult | null;
  siteTree: SiteTree | null;
  coverage: SiteTreeCoverage | null;
  nodeScreenshots: Record<string, string>;
  mobileDevice: MobileDeviceInfo | null;
  isCredentialRequested: boolean;
  loginFields: LoginFieldInfo[];
  loginStatus: LoginStatus;
  loginError: string | null;
  // CAPTCHA state
  isCaptchaDetected: boolean;
  captchaUrl: string | null;
  // Retry & assessment state
  retryProgress: { current: number; total: number } | null;
  retryResults: RetryDetail[];
  qualityAssessment: QualityAssessment | null;
  // Re-execution review state
  isReviewingFailures: boolean;
  failureReviewData: FailureReviewData | null;
  // Context gathering state
  isContextGatheringPending: boolean;
  contextQuestions: ContextQuestions | null;
  // Iterate mode state
  priorSessionLoaded: boolean;
  priorResults: PriorResultsData | null;
  // Page selection gate state
  isPageSelectionPending: boolean;
  discoveredPages: DiscoveredPage[];
  // API testing knowledge state
  apiTestingKnowledge: Record<string, any> | null;
  // Agent activity state
  agentActive: boolean;
  agentTask: string | null;
  agentToolEvents: AgentToolActivity[];
  agentFindings: AgentInvestigationFindings | null;
  // Per-page iterative testing state
  currentPageIndex: number | null;
  totalPages: number | null;
  currentPageTitle: string | null;
  pageResults: PageTestResult[];
  currentPageTestCases: TestCaseIdentified[];
  currentPageCode: { file_name: string; code: string } | null;
  // Per-page HITL review state
  isTestCaseReviewPending: boolean;
  isCodeReviewPending: boolean;
  // Returning user state (Phase 2 + 3)
  sessionHistory: SessionHistorySummary | null;
  isActionRequired: boolean;
  availableActions: TestingAction[];
  existingCodeInfo: ExistingCodeInfo[];
  // LiveKit state
  livekitToken: string | null;
  livekitUrl: string | null;
  livekitRoom: string | null;
  // Actions
  approve: () => void;
  reject: (feedback?: string) => void;
  endSession: () => void;
  cancel: () => void;
  addStep: (step: { action_id: string; parameters: Record<string, any>; description: string }) => void;
  submitCredentials: (username: string, password: string) => void;
  skipLogin: () => void;
  submitContext: (context: { app_description?: string; testing_goals?: string[]; key_user_journeys?: string[]; business_rules?: string[] }) => void;
  skipContextGathering: () => void;
  solveCaptcha: () => void;
  skipCaptcha: () => void;
  approveReexecution: () => void;
  skipReexecution: () => void;
  submitPageSelection: (nodeIds: string[]) => void;
  approveTestCases: (modifiedTestCases?: TestCaseIdentified[]) => void;
  approveCode: (modifiedCode?: string) => void;
  selectAction: (action: string) => void;
}

export type OnChatMessageCallback = (msg: Partial<ChatMessage>) => void;

export function useTestingSession(
  sessionId: string | null,
  onChatMessage?: OnChatMessageCallback,
): UseTestingSessionReturn {
  const [phase, setPhase] = useState<TestingSessionPhase>('initializing');
  const [steps, setSteps] = useState<TestingSessionStep[]>([]);
  const [stepResults, setStepResults] = useState<TestingStepResult[]>([]);
  const [liveStreamUrl, setLiveStreamUrl] = useState<string | null>(null);
  const [vncUrl, setVncUrl] = useState<string | null>(null);
  const [isApprovalPending, setIsApprovalPending] = useState(false);
  const [approvalMessage, setApprovalMessage] = useState('');
  const [domAnalysis, setDomAnalysis] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [duration, setDuration] = useState(0);
  const [summary, setSummary] = useState<any>(null);
  const [generatedCode, setGeneratedCode] = useState<any>(null);
  const [idleRemaining, setIdleRemaining] = useState<number | null>(null);
  const [apiStepResults, setApiStepResults] = useState<APIStepResult[]>([]);
  const [apiDiscovery, setApiDiscovery] = useState<APIDiscoveryResult | null>(null);
  const [siteTree, setSiteTree] = useState<SiteTree | null>(null);
  const [coverage, setCoverage] = useState<SiteTreeCoverage | null>(null);
  const [nodeScreenshots, setNodeScreenshots] = useState<Record<string, string>>({});
  const [mobileDevice, setMobileDevice] = useState<MobileDeviceInfo | null>(null);
  const [isCredentialRequested, setIsCredentialRequested] = useState(false);
  const [loginFields, setLoginFields] = useState<LoginFieldInfo[]>([]);
  const [loginStatus, setLoginStatus] = useState<LoginStatus>('none');
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isCaptchaDetected, setIsCaptchaDetected] = useState(false);
  const [captchaUrl, setCaptchaUrl] = useState<string | null>(null);
  const [retryProgress, setRetryProgress] = useState<{ current: number; total: number } | null>(null);
  const [retryResults, setRetryResults] = useState<RetryDetail[]>([]);
  const [qualityAssessment, setQualityAssessment] = useState<QualityAssessment | null>(null);
  const [isReviewingFailures, setIsReviewingFailures] = useState(false);
  const [failureReviewData, setFailureReviewData] = useState<FailureReviewData | null>(null);
  const [isContextGatheringPending, setIsContextGatheringPending] = useState(false);
  const [contextQuestions, setContextQuestions] = useState<ContextQuestions | null>(null);
  const [strategistInsights, setStrategistInsights] = useState<StrategistInsight[]>([]);
  const [priorSessionLoaded, setPriorSessionLoaded] = useState(false);
  const [priorResults, setPriorResults] = useState<PriorResultsData | null>(null);
  const [isPageSelectionPending, setIsPageSelectionPending] = useState(false);
  const [discoveredPages, setDiscoveredPages] = useState<DiscoveredPage[]>([]);
  const [apiTestingKnowledge, setApiTestingKnowledge] = useState<Record<string, any> | null>(null);
  const [agentActive, setAgentActive] = useState(false);
  const [agentTask, setAgentTask] = useState<string | null>(null);
  const [agentToolEvents, setAgentToolEvents] = useState<AgentToolActivity[]>([]);
  const [agentFindings, setAgentFindings] = useState<AgentInvestigationFindings | null>(null);
  // Per-page iterative testing state
  const [currentPageIndex, setCurrentPageIndex] = useState<number | null>(null);
  const [totalPages, setTotalPages] = useState<number | null>(null);
  const [currentPageTitle, setCurrentPageTitle] = useState<string | null>(null);
  const [pageResults, setPageResults] = useState<PageTestResult[]>([]);
  const [currentPageTestCases, setCurrentPageTestCases] = useState<TestCaseIdentified[]>([]);
  const [currentPageCode, setCurrentPageCode] = useState<{ file_name: string; code: string } | null>(null);
  // Per-page HITL review state
  const [isTestCaseReviewPending, setIsTestCaseReviewPending] = useState(false);
  const [isCodeReviewPending, setIsCodeReviewPending] = useState(false);
  // Returning user state (Phase 2 + 3)
  const [sessionHistory, setSessionHistory] = useState<SessionHistorySummary | null>(null);
  const [isActionRequired, setIsActionRequired] = useState(false);
  const [availableActions, setAvailableActions] = useState<TestingAction[]>([]);
  const [existingCodeInfo, setExistingCodeInfo] = useState<ExistingCodeInfo[]>([]);
  // LiveKit state
  const [livekitToken, setLivekitToken] = useState<string | null>(null);
  const [livekitUrl, setLivekitUrl] = useState<string | null>(null);
  const [livekitRoom, setLivekitRoom] = useState<string | null>(null);

  const eventSourceRef = useRef<EventSource | null>(null);
  const startTimeRef = useRef(Date.now());
  const phaseRef = useRef<TestingSessionPhase>('initializing');
  const emittedPhaseRef = useRef<TestingSessionPhase | null>(null);
  const receivedDoneRef = useRef(false);
  const onChatMessageRef = useRef(onChatMessage);
  onChatMessageRef.current = onChatMessage;
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track the active session ID to discard stale SSE events after a switch
  const activeSessionIdRef = useRef<string | null>(sessionId);

  // Helper to emit a testing event as a chat message
  const emitChatEvent = useCallback((card: TestingEventCard, content: string = '') => {
    onChatMessageRef.current?.({
      role: 'assistant',
      content,
      testingEvent: card,
    });
  }, []);

  // Duration timer
  useEffect(() => {
    if (!sessionId) return;
    startTimeRef.current = Date.now();

    const interval = setInterval(() => {
      if (phase !== 'completed' && phase !== 'failed' && phase !== 'cancelled') {
        setDuration(Date.now() - startTimeRef.current);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [sessionId, phase]);

  // Reset all session state when sessionId changes to prevent cross-session bleed
  useEffect(() => {
    activeSessionIdRef.current = sessionId;
    if (!sessionId) return;
    // Full state reset for the new session
    setPhase('initializing');
    setSteps([]);
    setStepResults([]);
    setLiveStreamUrl(null);
    setVncUrl(null);
    setIsApprovalPending(false);
    setApprovalMessage('');
    setDomAnalysis(null);
    setError(null);
    setDuration(0);
    setSummary(null);
    setGeneratedCode(null);
    setIdleRemaining(null);
    setApiStepResults([]);
    setApiDiscovery(null);
    setSiteTree(null);
    setCoverage(null);
    setNodeScreenshots({});
    setMobileDevice(null);
    setIsCredentialRequested(false);
    setLoginFields([]);
    setLoginStatus('none');
    setLoginError(null);
    setIsCaptchaDetected(false);
    setCaptchaUrl(null);
    setRetryProgress(null);
    setRetryResults([]);
    setQualityAssessment(null);
    setIsReviewingFailures(false);
    setFailureReviewData(null);
    setIsContextGatheringPending(false);
    setContextQuestions(null);
    setStrategistInsights([]);
    setPriorSessionLoaded(false);
    setPriorResults(null);
    setIsPageSelectionPending(false);
    setDiscoveredPages([]);
    setApiTestingKnowledge(null);
    setAgentActive(false);
    setAgentTask(null);
    setAgentToolEvents([]);
    setAgentFindings(null);
    setCurrentPageIndex(null);
    setTotalPages(null);
    setCurrentPageTitle(null);
    setPageResults([]);
    setCurrentPageTestCases([]);
    setCurrentPageCode(null);
    setIsTestCaseReviewPending(false);
    setIsCodeReviewPending(false);
    setSessionHistory(null);
    setIsActionRequired(false);
    setAvailableActions([]);
    setExistingCodeInfo([]);
    setLivekitToken(null);
    setLivekitUrl(null);
    setLivekitRoom(null);
    phaseRef.current = 'initializing';
    emittedPhaseRef.current = null;
    receivedDoneRef.current = false;
    startTimeRef.current = Date.now();
  }, [sessionId]);

  // SSE connection with reconnect
  useEffect(() => {
    if (!sessionId) return;

    const MAX_RECONNECT_ATTEMPTS = 10;
    const BASE_DELAY_MS = 1000; // 1s, 2s, 4s, 8s... capped at 30s

    reconnectAttemptRef.current = 0;
    const boundSessionId = sessionId; // capture for stale-event guard

    const connectSSE = () => {
      const streamUrl = testingService.getStreamUrl(sessionId);
      console.log('[useTestingSession] Connecting to SSE:', streamUrl, 'attempt:', reconnectAttemptRef.current);
      const es = new EventSource(streamUrl, { withCredentials: true });
      eventSourceRef.current = es;

      receivedDoneRef.current = false;

      const handleEvent = (eventType: string, data: any) => {
      // Discard events from a previous session after a switch
      if (activeSessionIdRef.current !== boundSessionId) {
        console.log('[useTestingSession] Discarding stale event for', boundSessionId, '(active:', activeSessionIdRef.current, ')');
        return;
      }
      switch (eventType) {
        case 'phase_change': {
          const newPhase = data.phase as TestingSessionPhase;
          phaseRef.current = newPhase;
          setPhase(newPhase);
          if (newPhase === 'awaiting_approval') {
            setIsApprovalPending(true);
          } else {
            setIsApprovalPending(false);
          }
          // Only emit a chat card when the phase actually changes —
          // repeated events with the same phase are progress updates.
          if (newPhase !== emittedPhaseRef.current) {
            emittedPhaseRef.current = newPhase;
            emitChatEvent(
              { type: 'phase_change', data },
              `Session entered **${(newPhase as string).replace(/_/g, ' ')}** phase`,
            );
          }
          break;
        }

        case 'live_stream_ready':
          setLiveStreamUrl(data.live_stream_url || null);
          setVncUrl(data.vnc_url || null);
          if (data.livekit_token) setLivekitToken(data.livekit_token);
          if (data.livekit_url) setLivekitUrl(data.livekit_url);
          if (data.livekit_room) setLivekitRoom(data.livekit_room);
          break;

        case 'navigation':
          // Could track navigation state if needed
          break;

        case 'dom_analysis':
          setDomAnalysis(data);
          break;

        case 'steps_generated':
          setSteps((prev) => [
            ...prev,
            ...data.steps.map((s: any, i: number) => ({
              ...s,
              index: prev.length + i,
              status: 'pending' as const,
            })),
          ]);
          emitChatEvent(
            { type: 'steps_generated', data },
            `Generated ${data.steps?.length || 0} test steps`,
          );
          break;

        case 'approval_required':
          setIsApprovalPending(true);
          setApprovalMessage(data.message || 'Approve to execute steps');
          emitChatEvent(
            { type: 'approval_required', data },
            data.message || 'Approve to execute steps',
          );
          break;

        case 'step_executing':
          setStepResults((prev) => {
            const existing = prev.find((r) => r.step_index === data.step_index);
            if (existing) {
              return prev.map((r) =>
                r.step_index === data.step_index ? { ...r, status: 'running' } : r
              );
            }
            return [
              ...prev,
              {
                step_index: data.step_index,
                action_id: data.action_id,
                status: 'running',
              },
            ];
          });
          emitChatEvent(
            { type: 'step_executing', data },
            `Executing step ${(data.step_index ?? 0) + 1}: ${data.description || data.action_id || ''}`,
          );
          break;

        case 'step_completed':
          setStepResults((prev) =>
            prev.map((r) =>
              r.step_index === data.step_index
                ? {
                    ...r,
                    status: data.status,
                    duration_ms: data.duration_ms,
                    screenshot_b64: data.screenshot_b64,
                    error: data.error,
                    healed: data.healed,
                  }
                : r
            )
          );
          emitChatEvent(
            { type: 'step_completed', data },
            `Step ${(data.step_index ?? 0) + 1} ${data.status === 'passed' ? 'passed' : 'failed'}${data.duration_ms ? ` (${data.duration_ms}ms)` : ''}`,
          );
          break;

        case 'step_healed':
          setStepResults((prev) =>
            prev.map((r) =>
              r.step_index === data.step_index
                ? {
                    ...r,
                    healed: true,
                    old_selector: data.old_selector,
                    new_selector: data.new_selector,
                    healing_strategy: data.strategy,
                  }
                : r
            )
          );
          emitChatEvent(
            { type: 'step_healed', data },
            `Self-healed step ${(data.step_index ?? 0) + 1}: selector updated`,
          );
          break;

        case 'code_generated':
          setGeneratedCode(data);
          emitChatEvent(
            { type: 'code_generated', data },
            'Test automation code generated',
          );
          break;

        case 'tms_entity_created':
          // Could track TMS entities
          break;

        case 'api_discovery':
          setApiDiscovery(data as APIDiscoveryResult);
          emitChatEvent(
            { type: 'api_discovery', data },
            `Discovered ${data.endpoints_found || 0} API endpoints`,
          );
          break;

        case 'api_config_request':
          setIsCredentialRequested(true);
          emitChatEvent(
            { type: 'api_config_request', data },
            'Configure API testing parameters',
          );
          break;

        case 'api_batch_completed':
          emitChatEvent(
            { type: 'api_batch_completed', data },
            `Batch ${data.batch_number || '?'}: ${data.batch_passed || 0}/${(data.batch_passed || 0) + (data.batch_failed || 0)} passed`,
          );
          break;

        case 'strategist_insight':
          setStrategistInsights((prev) => [...prev, data as StrategistInsight]);
          emitChatEvent(
            { type: 'strategist_insight', data },
            `Strategist (batch ${data.batch_number || '?'}): ${data.reasoning || data.action || ''}`,
          );
          break;

        case 'api_analysis_complete':
          emitChatEvent(
            { type: 'api_analysis_complete', data },
            `API Analysis: ${data.pass_rate || 0}% pass rate — Grade ${data.grade || '?'}`,
          );
          break;

        case 'api_testing_knowledge':
          setApiTestingKnowledge(data);
          emitChatEvent(
            { type: 'api_testing_knowledge', data },
            `Testing knowledge: ${data.tested_endpoints}/${data.total_endpoints} endpoints tested across ${data.total_sessions} sessions`,
          );
          break;

        case 'api_step_executing':
          setApiStepResults((prev) => [
            ...prev,
            {
              step_index: data.step_index,
              action_id: data.action_id,
              status: 'running',
              request: data.request,
              description: data.description,
              group: data.group,
              total_steps: data.total_steps,
            },
          ]);
          break;

        case 'api_step_completed':
          setApiStepResults((prev) =>
            prev.map((r) =>
              r.step_index === data.step_index
                ? {
                    ...r,
                    status: data.status,
                    duration_ms: data.duration_ms,
                    error: data.error,
                    request: data.request || r.request,
                    response: data.response,
                    category: data.category || r.category,
                    description: data.description || r.description,
                    group: data.group || r.group,
                    total_steps: data.total_steps || r.total_steps,
                  }
                : r
            )
          );
          // Results shown in right-side APIExecutionPanel, not as chat cards
          break;

        case 'site_tree_node_discovered': {
          const nodeId: string = data.node_id;
          const parentId: string | null = data.parent_id ?? null;
          setSiteTree((prev) => {
            const node: import('../types').SiteTreeNode = {
              id: nodeId,
              url: data.url,
              parent_id: parentId,
              depth: data.depth,
              title: data.title,
              children_ids: [] as string[],
              elements_count: data.elements_count ?? 0,
              forms_count: data.forms_count ?? 0,
              links_count: data.links_count ?? 0,
              test_status: 'pending',
              steps_count: 0,
              passed_count: 0,
              failed_count: 0,
              page_description: data.page_description ?? '',
              screenshot_available: data.screenshot_available ?? false,
              requires_auth: data.requires_auth ?? false,
              is_login_page: data.is_login_page ?? false,
            };
            if (!prev) {
              return {
                root_id: parentId ? null : nodeId,
                total_nodes: 1,
                nodes: { [nodeId]: node },
              };
            }
            const nodes: Record<string, import('../types').SiteTreeNode> = { ...prev.nodes, [nodeId]: node };
            // Update parent's children_ids
            if (parentId && nodes[parentId]) {
              const parent = nodes[parentId];
              if (!parent.children_ids.includes(nodeId)) {
                nodes[parentId] = {
                  ...parent,
                  children_ids: [...parent.children_ids, nodeId],
                };
              }
            }
            return {
              root_id: prev.root_id || nodeId,
              total_nodes: Object.keys(nodes).length,
              nodes,
            };
          });
          break;
        }

        case 'node_screenshot':
          if (data.node_id && data.screenshot_b64) {
            setNodeScreenshots((prev) => ({ ...prev, [data.node_id]: data.screenshot_b64 }));
          }
          break;

        case 'site_tree_built':
          setSiteTree(data as SiteTree);
          break;

        case 'site_tree_node_status':
          setSiteTree((prev) => {
            if (!prev || !prev.nodes[data.node_id]) return prev;
            return {
              ...prev,
              nodes: {
                ...prev.nodes,
                [data.node_id]: {
                  ...prev.nodes[data.node_id],
                  test_status: data.test_status,
                  steps_count: data.steps_count ?? prev.nodes[data.node_id].steps_count,
                  passed_count: data.passed_count ?? prev.nodes[data.node_id].passed_count,
                  failed_count: data.failed_count ?? prev.nodes[data.node_id].failed_count,
                },
              },
            };
          });
          break;

        case 'site_tree_coverage':
          setCoverage(data as SiteTreeCoverage);
          break;

        case 'session_interactive':
          setIdleRemaining(data.idle_timeout || 300);
          break;

        case 'interactive_step_processing':
          if (data.status === 'executing' || data.status === 'converting') {
            setSteps(prev => {
              // Avoid duplicating if already added
              const exists = prev.some(
                s => s.description === data.description && s.status === 'running'
              );
              if (exists) return prev;
              return [...prev, {
                action_id: 'nl_command',
                description: data.description,
                parameters: {},
                index: prev.length,
                status: 'running' as const,
              }];
            });
          } else if (data.status === 'completed') {
            setSteps(prev => prev.map(s =>
              s.description === data.description && s.status === 'running'
                ? { ...s, status: 'passed' as const }
                : s
            ));
          } else if (data.status === 'failed' || data.status === 'no_steps') {
            setSteps(prev => prev.map(s =>
              s.description === data.description && s.status === 'running'
                ? { ...s, status: 'failed' as const }
                : s
            ));
            setError(data.error || data.message || 'Step failed');
          }
          break;

        case 'interactive_keepalive':
          setIdleRemaining(data.idle_remaining ?? null);
          break;

        case 'session_idle_timeout':
          setIdleRemaining(0);
          break;

        case 'mobile_session_ready':
          setMobileDevice({
            session_id: data.session_id,
            device_name: data.device_name,
            platform: data.platform,
            app_package: data.app_package,
            appium_url: data.appium_url,
          });
          setLiveStreamUrl(data.live_stream_url || null);
          setVncUrl(data.vnc_url || null);
          break;

        case 'app_installed':
          setMobileDevice(prev => prev ? { ...prev, app_package: data.app_package } : null);
          break;

        case 'credential_request':
          setIsCredentialRequested(true);
          setLoginFields(data.fields || []);
          setLoginStatus('requested');
          setLoginError(null);
          emitChatEvent(
            { type: 'credential_request', data },
            'Login detected — credentials required',
          );
          break;

        case 'login_executing':
          setIsCredentialRequested(false);
          setLoginStatus('executing');
          break;

        case 'login_success':
          setLoginStatus('success');
          setIsCredentialRequested(false);
          setLoginError(null);
          emitChatEvent(
            { type: 'login_status', data: { ...data, success: true } },
            'Login successful',
          );
          break;

        case 'login_failed':
          setLoginStatus('failed');
          setLoginError(data.error || data.message || 'Login failed');
          if (data.can_retry) {
            setIsCredentialRequested(true);
          }
          emitChatEvent(
            { type: 'login_status', data: { ...data, success: false } },
            `Login failed: ${data.error || data.message || 'Unknown error'}`,
          );
          break;

        case 'login_profile_restored':
          setLoginStatus('success');
          setIsCredentialRequested(false);
          setLoginError(null);
          break;

        case 'login_detected':
          // Informational; the credential_request event triggers the UI
          break;

        case 'login_auto_fill':
          // Pre-configured credentials being used — no UI action needed
          break;

        case 'prior_results_loaded':
          setPriorSessionLoaded(true);
          setPriorResults(data as PriorResultsData);
          emitChatEvent(
            { type: 'prior_results_loaded', data },
            `Loaded ${data.total_steps || 0} steps from previous session (${data.passed_count || 0} passed, ${data.failed_count || 0} failed)`,
          );
          break;

        case 'context_questions':
          setContextQuestions(data as ContextQuestions);
          setIsContextGatheringPending(true);
          break;

        case 'context_loaded':
          // Saved context auto-loaded — informational, no HITL block
          setIsContextGatheringPending(false);
          setContextQuestions(null);
          break;

        case 'page_selection_required':
          if (data.auto_selected) {
            // Auto-execute mode: pages auto-selected, no user action needed
            setIsPageSelectionPending(false);
            setDiscoveredPages(data.nodes || []);
            emitChatEvent(
              { type: 'page_selection_required', data },
              data.message || `Auto-selected ${(data.nodes || []).length} pages for testing`,
            );
          } else {
            setIsPageSelectionPending(true);
            setDiscoveredPages(data.nodes || []);
            emitChatEvent(
              { type: 'page_selection_required', data },
              data.message || 'Select pages to test',
            );
          }
          break;

        case 'otp_needed':
          // OTP page detected — credential_request with otp field will follow if manual
          break;

        case 'otp_auto_fill':
          // OTP auto-filled from provider — informational
          break;

        case 'otp_status':
          // OTP provider status update (e.g., "Polling IMAP for OTP email...")
          emitChatEvent(
            { type: 'otp_status', data },
            data.message || 'OTP status update',
          );
          break;

        case 'execution_login_detected':
          // Login page detected during test execution — auto-handling
          break;

        case 'captcha_detected':
          setIsCaptchaDetected(true);
          setCaptchaUrl(data.url || null);
          emitChatEvent(
            { type: 'captcha_detected', data },
            'CAPTCHA detected on page',
          );
          break;

        case 'review_failures':
          setIsReviewingFailures(true);
          setFailureReviewData(data as FailureReviewData);
          emitChatEvent(
            { type: 'review_failures', data },
            `${data.failed_count || 0} step(s) failed — review required`,
          );
          break;

        case 'reexecution_approved':
        case 'reexecution_skipped':
          setIsReviewingFailures(false);
          break;

        case 'retry_phase_started':
          setRetryProgress({ current: 0, total: data.total_retries || 0 });
          break;

        case 'retry_started':
          setRetryProgress({ current: data.index || 0, total: data.total || 0 });
          break;

        case 'retry_completed':
          setRetryProgress({ current: data.index || 0, total: data.total || 0 });
          setRetryResults((prev) => [
            ...prev,
            {
              test_case_id: data.test_case_id,
              node_id: data.node_id,
              classification: data.classification,
              retry_status: data.retry_status,
              original_error: data.original_error,
              retry_error: data.retry_error,
            },
          ]);
          // When a retry passes (flaky), update the step in stepResults
          // so the passed/failed counters reflect the corrected state.
          if (data.retry_status === 'PASSED' && data.original_step_index != null) {
            setStepResults((prev) =>
              prev.map((r) =>
                r.step_index === data.original_step_index && r.status === 'failed'
                  ? { ...r, status: 'passed' }
                  : r
              )
            );
          }
          break;

        case 'quality_assessment':
          setQualityAssessment(data as QualityAssessment);
          setRetryProgress(null);
          emitChatEvent(
            { type: 'quality_assessment', data },
            `Quality assessment: Grade **${data.grade}** — ${data.pass_rate}% pass rate`,
          );
          break;

        case 'session_context':
          // Session context emitted after completion — update generated code
          // if refinement produced new code
          if (data.generated_code) {
            setGeneratedCode(data.generated_code);
          }
          break;

        case 'error':
          setError(data.message);
          if (!data.recoverable) {
            phaseRef.current = 'failed';
            setPhase('failed');
          }
          emitChatEvent(
            { type: 'session_error', data },
            data.message || 'An error occurred',
          );
          break;

        case 'strategy_resolved':
          emitChatEvent(
            { type: 'strategy_resolved', data },
            `Testing strategy: **${(data.strategy_type || 'exploratory').toUpperCase()}** (max ${data.max_pages} pages, ${data.time_budget_minutes} min budget)`,
          );
          break;

        case 'pipeline_started':
          emitChatEvent(
            { type: 'pipeline_started', data },
            `QA Pipeline started — ${data.total_stages || 7} stages`,
          );
          break;

        case 'pipeline_stage_started': {
          const stageLabel = (data.stage || '').replace(/_/g, ' ');
          emitChatEvent(
            { type: 'pipeline_stage_started', data },
            `Pipeline stage ${(data.stage_index ?? 0) + 1}/${data.total_stages || 7}: **${stageLabel}** started`,
          );
          break;
        }

        case 'pipeline_stage_complete': {
          const completeLabel = (data.stage || '').replace(/_/g, ' ');
          emitChatEvent(
            { type: 'pipeline_stage_complete', data },
            `Pipeline stage **${completeLabel}** completed${data.duration_ms ? ` (${data.duration_ms}ms)` : ''}`,
          );
          break;
        }

        case 'pipeline_stage_error': {
          const errorLabel = (data.stage || '').replace(/_/g, ' ');
          emitChatEvent(
            { type: 'pipeline_stage_error', data },
            `Pipeline stage **${errorLabel}** failed: ${data.error || 'Unknown error'}`,
          );
          break;
        }

        case 'pipeline_complete':
          emitChatEvent(
            { type: 'pipeline_complete', data },
            `QA Pipeline ${data.status || 'completed'} — ${data.stages_completed || 0} stages in ${Math.round((data.total_duration_ms || 0) / 1000)}s`,
          );
          break;

        case 'strategy_abort':
          emitChatEvent(
            { type: 'strategy_abort', data },
            `Testing stopped: ${data.reason}`,
          );
          break;

        case 'agent_task_started':
          setAgentActive(true);
          setAgentTask(data.task || null);
          setAgentToolEvents([]);
          break;

        case 'agent_token':
          // Streaming tokens from agent — not rendered individually
          break;

        case 'agent_tool_start':
          setAgentToolEvents((prev) => [
            ...prev,
            {
              task: data.task || '',
              tool: data.tool || '',
              tool_use_id: data.tool_use_id || '',
              input: data.input,
              phase: 'start' as const,
            },
          ]);
          break;

        case 'agent_tool_end':
          setAgentToolEvents((prev) =>
            prev.map((e) =>
              e.tool_use_id === data.tool_use_id && e.phase === 'start'
                ? { ...e, phase: 'end' as const, output: data.output, is_error: data.is_error }
                : e
            )
          );
          break;

        case 'agent_task_complete':
          setAgentActive(false);
          break;

        case 'agent_findings':
          setAgentFindings(data as AgentInvestigationFindings);
          break;

        case 'agent_error':
          setAgentActive(false);
          break;

        // Per-page iterative testing events
        case 'per_page_flow_started':
          setTotalPages(data.total_pages || 0);
          break;

        case 'page_testing_started':
          setCurrentPageIndex(data.page_index ?? 0);
          setCurrentPageTitle(data.page_title || '');
          setCurrentPageTestCases([]);
          setCurrentPageCode(null);
          emitChatEvent(
            { type: 'page_testing_started', data },
            `Testing page ${(data.page_index ?? 0) + 1}/${data.total_pages || '?'}: **${data.page_title || data.page_url}**`,
          );
          break;

        case 'test_cases_identified':
          setCurrentPageTestCases(data.test_cases || []);
          emitChatEvent(
            { type: 'test_cases_identified', data },
            `Identified ${(data.test_cases || []).length} test cases for ${data.page_title || data.page_url}`,
          );
          break;

        case 'test_case_review_required':
          setIsTestCaseReviewPending(true);
          setCurrentPageTestCases(data.test_cases || []);
          emitChatEvent(
            { type: 'test_case_review_required', data },
            data.message || `Review ${(data.test_cases || []).length} test cases for ${data.page_title || data.page_url}`,
          );
          break;

        case 'page_code_generated':
          setCurrentPageCode({
            file_name: data.file_name || '',
            code: data.code || '',
          });
          emitChatEvent(
            { type: 'page_code_generated', data },
            `Generated test code: ${data.file_name || 'test.py'}`,
          );
          break;

        case 'code_review_required':
          setIsCodeReviewPending(true);
          setCurrentPageCode({
            file_name: data.file_name || '',
            code: data.code || '',
          });
          emitChatEvent(
            { type: 'code_review_required', data },
            data.message || `Review generated code: ${data.file_name || 'test.py'}`,
          );
          break;

        case 'page_execution_started':
          emitChatEvent(
            { type: 'page_execution_started', data },
            `Executing ${data.total_tests || 0} tests for ${data.page_title || data.page_url}`,
          );
          break;

        case 'page_test_result':
          // Individual test result within a page — already handled by step_completed
          break;

        case 'page_testing_completed':
          setPageResults((prev) => [
            ...prev,
            {
              page_url: data.page_url || '',
              page_title: data.page_title || '',
              node_id: data.node_id || '',
              page_index: data.page_index ?? 0,
              total_pages: data.total_pages ?? 0,
              test_cases: data.test_cases || [],
              code_file: data.code_file || '',
              passed: data.passed ?? 0,
              failed: data.failed ?? 0,
              skipped: data.skipped ?? 0,
              total: data.total ?? 0,
              step_results: data.step_results || [],
            },
          ]);
          emitChatEvent(
            { type: 'page_testing_completed', data },
            `Page "${data.page_title || data.page_url}": ${data.passed ?? 0} passed, ${data.failed ?? 0} failed`,
          );
          break;

        case 'all_pages_completed':
          emitChatEvent(
            { type: 'all_pages_completed', data },
            `All ${data.total_pages || 0} pages tested: ${data.total_passed ?? 0} passed, ${data.total_failed ?? 0} failed`,
          );
          break;

        // Returning user events (Phase 2 + 3)
        case 'existing_code_detected':
          setExistingCodeInfo((prev) => [...prev, data as ExistingCodeInfo]);
          emitChatEvent(
            { type: 'existing_code_detected', data },
            `Found existing test file: ${data.file_name || 'test.py'} (${data.test_count || 0} tests) — agent will update`,
          );
          break;

        case 'session_history_summary':
          setSessionHistory(data as SessionHistorySummary);
          emitChatEvent(
            { type: 'session_history_summary', data },
            `Returning session for ${data.domain}: ${data.total_pages_tested || 0} pages tested across ${data.total_sessions || 0} sessions`,
          );
          break;

        case 'testing_action_required':
          setIsActionRequired(true);
          setAvailableActions(data.actions || []);
          emitChatEvent(
            { type: 'testing_action_required', data },
            `Select testing action for ${data.domain}`,
          );
          break;

        case 'complete':
          phaseRef.current = 'completed';
          setPhase('completed');
          setDuration(data.duration_ms || Date.now() - startTimeRef.current);
          setSummary(data.summary);
          emitChatEvent(
            { type: 'session_complete', data },
            'Testing session completed',
          );
          break;

        case 'keepalive':
          // No-op, just prevents timeout
          break;
      }
    };

    // Listen for named events
    const eventTypes = [
      'session_created', 'phase_change', 'live_stream_ready', 'navigation',
      'dom_analysis', 'steps_generated', 'approval_required', 'step_executing',
      'step_completed', 'step_healed', 'exploration_page', 'tms_entity_created',
      'code_generated', 'code_generation_skipped', 'session_analysis',
      'api_discovery', 'api_config_request', 'api_testing_knowledge', 'api_step_executing', 'api_step_completed',
      'api_batch_completed', 'api_analysis_complete',
      'site_tree_node_discovered', 'node_screenshot', 'site_tree_built', 'site_tree_node_status', 'site_tree_coverage', 'page_selection_required',
      'session_interactive', 'interactive_keepalive',
      'interactive_step_processing', 'session_idle_timeout',
      'mobile_session_ready', 'app_installed',
      'prior_results_loaded',
      'context_questions', 'context_loaded',
      'credential_request', 'login_profile_restored', 'login_detected', 'login_executing', 'login_success', 'login_failed',
      'login_auto_fill', 'otp_needed', 'otp_auto_fill', 'otp_status',
      'execution_login_detected', 'captcha_detected',
      'review_failures', 'reexecution_approved', 'reexecution_skipped',
      'retry_phase_started', 'retry_started', 'retry_completed', 'quality_assessment',
      'session_context',
      'pipeline_started', 'pipeline_stage_started', 'pipeline_stage_complete',
      'pipeline_stage_error', 'pipeline_complete',
      'agent_task_started', 'agent_token', 'agent_tool_start', 'agent_tool_end',
      'agent_task_complete', 'agent_findings', 'agent_error',
      'per_page_flow_started', 'page_testing_started', 'test_cases_identified',
      'test_case_review_required',
      'page_code_generated', 'code_review_required',
      'page_execution_started', 'page_test_result',
      'page_testing_completed', 'all_pages_completed', 'page_video_ready',
      'error', 'complete', 'keepalive', 'done',
    ];

    for (const eventType of eventTypes) {
      es.addEventListener(eventType, (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          handleEvent(eventType, data);
        } catch {
          // Ignore parse errors
        }
      });
    }

    es.addEventListener('done', () => {
      receivedDoneRef.current = true;
      es.close();
    });

    es.onopen = () => {
      // Connection established — reset reconnect counter
      reconnectAttemptRef.current = 0;
    };

    es.onerror = (evt) => {
      console.warn('[useTestingSession] EventSource error, readyState:', es.readyState, evt);
      const currentPhase = phaseRef.current;
      const isTerminal = currentPhase === 'completed' || currentPhase === 'failed' || currentPhase === 'cancelled';

      // If session reached a terminal phase or we received the done sentinel,
      // the stream closing is expected — just close cleanly without error.
      if (isTerminal || receivedDoneRef.current) {
        es.close();
        return;
      }

      // If connection closed, defer the failure check briefly — the server may
      // have sent `complete` or `done` events that are still queued in the
      // browser's event loop and haven't been processed yet.
      if (es.readyState === EventSource.CLOSED) {
        es.close();
        setTimeout(() => {
          // Re-check after letting queued SSE events process
          if (receivedDoneRef.current) return;
          const deferredPhase = phaseRef.current;
          if (deferredPhase === 'completed' || deferredPhase === 'failed' || deferredPhase === 'cancelled') return;

          // Attempt reconnect with exponential backoff
          if (reconnectAttemptRef.current < MAX_RECONNECT_ATTEMPTS) {
            const delay = Math.min(
              BASE_DELAY_MS * Math.pow(2, reconnectAttemptRef.current),
              30000,
            );
            reconnectAttemptRef.current += 1;
            console.log(
              `[useTestingSession] Reconnecting in ${delay}ms (attempt ${reconnectAttemptRef.current}/${MAX_RECONNECT_ATTEMPTS})`,
            );
            reconnectTimerRef.current = setTimeout(() => {
              connectSSE();
            }, delay);
          } else {
            // Exhausted reconnect attempts
            setStepResults((prev) => {
              const hasResults = prev.length > 0;
              if (hasResults) {
                console.log('[useTestingSession] Stream closed with results — treating as completed');
                phaseRef.current = 'completed';
                setPhase('completed');
              } else {
                console.warn('[useTestingSession] Stream closed after max reconnect attempts');
                setError('Lost connection to testing session. Please try again.');
                phaseRef.current = 'failed';
                setPhase('failed');
              }
              return prev;
            });
          }
        }, 500);
      }
    };
    }; // end connectSSE

    connectSSE();

    return () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [sessionId]);

  // Actions
  const approve = useCallback(() => {
    if (sessionId) {
      testingService.approveSteps(sessionId).catch(console.error);
      setIsApprovalPending(false);
    }
  }, [sessionId]);

  const reject = useCallback(
    (feedback?: string) => {
      if (sessionId) {
        testingService.rejectSteps(sessionId, feedback).catch(console.error);
        setIsApprovalPending(false);
        setSteps([]); // Clear steps for regeneration
      }
    },
    [sessionId]
  );

  // Clear persisted session from localStorage when session reaches a terminal phase
  useEffect(() => {
    if (phase === 'completed' || phase === 'failed' || phase === 'cancelled') {
      localStorage.removeItem('qa_agent_active_testing_session');
    }
  }, [phase]);

  const endSession = useCallback(() => {
    if (sessionId) {
      testingService.endSession(sessionId).catch(console.error);
      localStorage.removeItem('qa_agent_active_testing_session');
    }
  }, [sessionId]);

  const cancel = useCallback(() => {
    if (sessionId) {
      testingService.cancelSession(sessionId).catch(console.error);
      phaseRef.current = 'cancelled';
      setPhase('cancelled');
      localStorage.removeItem('qa_agent_active_testing_session');
    }
  }, [sessionId]);

  const addStep = useCallback(
    (step: { action_id: string; parameters: Record<string, any>; description: string }) => {
      if (sessionId) {
        testingService.addStep(sessionId, step).catch(console.error);
      }
    },
    [sessionId]
  );

  const submitCredentials = useCallback(
    (username: string, password: string) => {
      if (sessionId) {
        setIsCredentialRequested(false);
        setLoginStatus('executing');
        testingService.submitCredentials(sessionId, username, password).catch((err) => {
          console.error(err);
          setLoginError('Failed to submit credentials');
          setIsCredentialRequested(true);
          setLoginStatus('requested');
        });
      }
    },
    [sessionId]
  );

  const skipLogin = useCallback(() => {
    if (sessionId) {
      setIsCredentialRequested(false);
      setLoginStatus('none');
      testingService.skipLogin(sessionId).catch(console.error);
    }
  }, [sessionId]);

  const submitContext = useCallback(
    (context: { app_description?: string; testing_goals?: string[]; key_user_journeys?: string[]; business_rules?: string[] }) => {
      if (sessionId) {
        setIsContextGatheringPending(false);
        setContextQuestions(null);
        testingService.submitContext(sessionId, context).catch(console.error);
      }
    },
    [sessionId]
  );

  const skipContextGathering = useCallback(() => {
    if (sessionId) {
      setIsContextGatheringPending(false);
      setContextQuestions(null);
      testingService.skipContext(sessionId).catch(console.error);
    }
  }, [sessionId]);

  const solveCaptcha = useCallback(() => {
    if (sessionId) {
      setIsCaptchaDetected(false);
      setCaptchaUrl(null);
      testingService.solveCaptcha(sessionId).catch(console.error);
    }
  }, [sessionId]);

  const skipCaptcha = useCallback(() => {
    if (sessionId) {
      setIsCaptchaDetected(false);
      setCaptchaUrl(null);
      testingService.skipCaptcha(sessionId).catch(console.error);
    }
  }, [sessionId]);

  const approveReexecution = useCallback(() => {
    if (sessionId) {
      setIsReviewingFailures(false);
      setFailureReviewData(null);
      testingService.approveReexecution(sessionId).catch(console.error);
    }
  }, [sessionId]);

  const skipReexecution = useCallback(() => {
    if (sessionId) {
      setIsReviewingFailures(false);
      setFailureReviewData(null);
      testingService.skipReexecution(sessionId).catch(console.error);
    }
  }, [sessionId]);

  const submitPageSelection = useCallback(
    (nodeIds: string[]) => {
      if (sessionId) {
        setIsPageSelectionPending(false);
        testingService.selectPages(sessionId, nodeIds).catch(console.error);
      }
    },
    [sessionId]
  );

  const approveTestCases = useCallback(
    (modifiedTestCases?: TestCaseIdentified[]) => {
      if (sessionId) {
        setIsTestCaseReviewPending(false);
        testingService.approveTestCases(
          sessionId,
          modifiedTestCases as Array<{ name: string; description: string; priority: string; category: string }>,
        ).catch(console.error);
      }
    },
    [sessionId]
  );

  const approveCode = useCallback(
    (modifiedCode?: string) => {
      if (sessionId) {
        setIsCodeReviewPending(false);
        testingService.approveCode(sessionId, modifiedCode).catch(console.error);
      }
    },
    [sessionId]
  );

  const selectAction = useCallback(
    (action: string) => {
      if (sessionId) {
        setIsActionRequired(false);
        testingService.selectAction(sessionId, action).catch(console.error);
      }
    },
    [sessionId]
  );

  return {
    phase,
    steps,
    stepResults,
    liveStreamUrl,
    vncUrl,
    isApprovalPending,
    approvalMessage,
    domAnalysis,
    error,
    duration,
    idleRemaining,
    summary,
    generatedCode,
    apiStepResults,
    apiDiscovery,
    siteTree,
    coverage,
    nodeScreenshots,
    mobileDevice,
    isCredentialRequested,
    loginFields,
    loginStatus,
    loginError,
    isCaptchaDetected,
    captchaUrl,
    retryProgress,
    retryResults,
    qualityAssessment,
    isReviewingFailures,
    failureReviewData,
    isContextGatheringPending,
    contextQuestions,
    priorSessionLoaded,
    priorResults,
    isPageSelectionPending,
    discoveredPages,
    apiTestingKnowledge,
    agentActive,
    agentTask,
    agentToolEvents,
    agentFindings,
    currentPageIndex,
    totalPages,
    currentPageTitle,
    pageResults,
    currentPageTestCases,
    currentPageCode,
    isTestCaseReviewPending,
    isCodeReviewPending,
    sessionHistory,
    isActionRequired,
    availableActions,
    existingCodeInfo,
    livekitToken,
    livekitUrl,
    livekitRoom,
    approve,
    reject,
    endSession,
    cancel,
    addStep,
    submitCredentials,
    skipLogin,
    submitContext,
    skipContextGathering,
    solveCaptcha,
    skipCaptcha,
    approveReexecution,
    skipReexecution,
    submitPageSelection,
    approveTestCases,
    approveCode,
    selectAction,
  };
}

export default useTestingSession;
